import { Effect, Schema, SchemaTransformation, Struct } from 'effect';
/**
 * Page content lives in its own collection, one document pr page:
 *
 *   sites/{siteId}/pages/{pageId}
 *
 * The site document keeps only the navigation metadata (see page.ts) so the
 * navbar and the router can render on the first snapshot, while the body is
 * fetched lazily by whoever actually visits the page.
 *
 * The same collection holds blog posts and the text of the site's policies
 * (terms and rules), discriminated by `kind`. Posts and policies are written
 * through the backend only (/admin/posts, /admin/policies); built-in pages are
 * still written by the client. See docs/plans/firestore_readonly_client.md.
 * A policy document's id is the policy id; its metadata stays in
 * siteData.policies, which booking needs before anything renders.
 */
const PageKindSchema = Schema.Literals(['page', 'post', 'policy']);
const PageFormatSchema = Schema.Literals(['markdown', 'html']);
const PageStatusSchema = Schema.Literals(['draft', 'published']);
const defaultStatus = (kind) => kind === 'post' ? 'draft' : 'published';
const text = Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed('')));
/**
 * `sites/{site}/pages/{id}` as stored. Every field is optional on the way in,
 * with the defaults `toPageDoc` has always applied - except `status`, whose
 * default depends on `kind` and is filled in by the transform below.
 */
const StoredPageFields = Schema.Struct({
    id: text, // 'aboutus' | 'findus' for pages, slug for posts, policy id
    kind: PageKindSchema.pipe(Schema.withDecodingDefaultType(Effect.succeed('page'))),
    // Markdown documents are legacy content carried over from the site document.
    // The rich text editor writes html, so that is the default.
    format: PageFormatSchema.pipe(Schema.withDecodingDefaultType(Effect.succeed('html'))),
    status: Schema.optional(PageStatusSchema),
    title: text,
    body: text,
    excerpt: text, // post listing teaser
    coverImage: text, // storage url from the media library
    tags: Schema.mutable(Schema.Array(Schema.String)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    publishedAt: text, // ISO, orders the blog index
    updatedAt: text, // ISO
    updatedBy: text, // uid
    author: text // display name, denormalised
});
/** The current document type: every field present. */
const PageDocFields = Schema.Struct({
    id: Schema.String,
    kind: PageKindSchema,
    format: PageFormatSchema,
    status: PageStatusSchema,
    title: Schema.String,
    body: Schema.String,
    excerpt: Schema.String,
    coverImage: Schema.String,
    tags: Schema.mutable(Schema.Array(Schema.String)),
    publishedAt: Schema.String,
    updatedAt: Schema.String,
    updatedBy: Schema.String,
    author: Schema.String
}).mapFields(Struct.map(Schema.mutableKey));
const PageDocSchema = StoredPageFields.pipe(Schema.decodeTo(Schema.toType(PageDocFields), SchemaTransformation.transform({
    // A post written without a status is a draft, anything else published -
    // the same kind-derived default `emptyPageDoc` uses.
    decode: (doc) => ({
        ...doc,
        tags: [...doc.tags],
        status: doc.status ?? defaultStatus(doc.kind)
    }),
    encode: (doc) => doc
})));
const toPageKind = (raw) => {
    return raw === 'post' || raw === 'policy' ? raw : 'page';
};
const toPageFormat = (raw) => {
    // Markdown documents are legacy content carried over from the site document.
    // The rich text editor writes html, so that is the safer default for a
    // document missing the field.
    return raw === 'markdown' ? 'markdown' : 'html';
};
const toPageStatus = (raw) => {
    return raw === 'published' ? 'published' : 'draft';
};
const toTags = (raw) => {
    if (!Array.isArray(raw))
        return [];
    return raw.filter((tag) => typeof tag === 'string' && !!tag);
};
/**
 * A blank document. Pages and policies are published by definition - they are
 * reachable the moment the navigation entry or the policy exists - while posts
 * start life as drafts so an unfinished document can never leak through the
 * public listing.
 */
const emptyPageDoc = (id, kind = 'page') => {
    return {
        id,
        kind,
        format: 'html',
        status: defaultStatus(kind),
        title: '',
        body: '',
        excerpt: '',
        coverImage: '',
        tags: [],
        publishedAt: '',
        updatedAt: '',
        updatedBy: '',
        author: ''
    };
};
const toPageDoc = (id, raw) => {
    const kind = toPageKind(raw?.kind);
    const page = emptyPageDoc(id, kind);
    if (!raw)
        return page;
    page.format = toPageFormat(raw.format);
    // Keep the kind-derived default when the field is absent, so a page document
    // written without a status does not silently become an admin-only draft.
    page.status = raw.status ? toPageStatus(raw.status) : page.status;
    page.title = raw.title || '';
    page.body = raw.body || '';
    page.excerpt = raw.excerpt || '';
    page.coverImage = raw.coverImage || '';
    page.tags = toTags(raw.tags);
    page.publishedAt = raw.publishedAt || '';
    page.updatedAt = raw.updatedAt || '';
    page.updatedBy = raw.updatedBy || '';
    page.author = raw.author || '';
    return page;
};
const isPublished = (page) => page.status === 'published';
/**
 * The id lives in the document as well as in its path - every writer has
 * always put it there - so the converter can take it from the data.
 */
const PageDocStored = {
    name: 'PageDoc',
    schema: PageDocSchema,
    legacy: (raw) => toPageDoc(raw?.id || '', raw),
    authority: 'schema'
};
export { emptyPageDoc, isPublished, PageDocSchema, PageDocStored, toPageDoc };
//# sourceMappingURL=pageContent.js.map